// MF-568 — Card 'pat-progress-panels' (2B): class progress panels. One row per student, three aligned panels (scale
// score, stanine, achievement band) with one column per period; the latest period is bold. Rows come long, one per
// student per sitting: { student_id, name, period, sort, score, band, stanine, year_level, test_date }.
import { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { SUMMARY_PALETTE, INK, FONT } from '../boardTheme'
import {
  STANINE_NOTE,
  bandLow,
  byBand,
  isNum,
  fmtStanine,
  fmtDate,
  tint,
  rangeColor
} from './patProgressShared'

const PANELS = [
  { key: 'score', title: 'Scale score' },
  { key: 'stanine', title: 'Stanine' },
  { key: 'band', title: 'Achievement band' }
]

const th = {
  fontSize: 11,
  fontWeight: 600,
  color: SUMMARY_PALETTE.muted,
  textAlign: 'center',
  px: 0.75,
  py: 0.5,
  whiteSpace: 'nowrap'
}
const gap = { width: 14, p: 0, border: 0 }

const PatProgressPanelsCard = ({ data }) => {
  const rows = useMemo(() => data?.rows || [], [data])

  const model = useMemo(() => {
    const periods = new Map() // period → sort
    const students = new Map() // id → { name, byPeriod }
    for (const r of rows) {
      periods.set(r.period, r.sort)
      if (!students.has(r.student_id))
        students.set(r.student_id, { id: r.student_id, name: r.name, byPeriod: new Map() })
      const s = students.get(r.student_id)
      const prev = s.byPeriod.get(r.period)
      // the latest row per (student, period) wins
      if (!prev || String(r.test_date) >= String(prev.test_date)) s.byPeriod.set(r.period, r)
    }
    const order = [...periods.entries()].sort((a, b) => a[1] - b[1]).map(([p]) => p)
    const list = [...students.values()]
    const lastScore = (s) => {
      for (let i = order.length - 1; i >= 0; i -= 1) {
        const c = s.byPeriod.get(order[i])
        if (c) return Number(c.score)
      }
      return -Infinity
    }
    list.sort((a, b) => lastScore(b) - lastScore(a) || a.name.localeCompare(b.name))
    const scores = rows.map((r) => Number(r.score)).filter(Number.isFinite)
    const bands = [...new Set(rows.map((r) => r.band).filter(Boolean))].sort(byBand)
    return {
      order,
      list,
      min: Math.min(...scores),
      max: Math.max(...scores),
      bands,
      anyStanine: rows.some((r) => isNum(r.stanine))
    }
  }, [rows])

  const { order, list, min, max, bands } = model
  const latest = order[order.length - 1]
  const bandTint = (b) => {
    const i = bands.indexOf(b)
    return tint(rangeColor(i, 0, Math.max(1, bands.length - 1)), 0.65)
  }
  const cell = (panel, c) => {
    if (!c) return { text: '–', bg: 'transparent' }
    if (panel === 'score')
      return {
        text: isNum(c.score) ? String(Math.round(Number(c.score) * 10) / 10) : '–',
        bg: isNum(c.score) ? tint(rangeColor(c.score, min, max)) : 'transparent'
      }
    if (panel === 'stanine') return { text: fmtStanine(c.stanine), bg: 'transparent' }
    return { text: c.band || '–', bg: c.band && bandLow(c.band) !== null ? bandTint(c.band) : 'transparent' }
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto', minWidth: 0, fontFamily: FONT }}>
      <Box
        component='table'
        sx={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: 'max-content', width: '100%' }}
      >
        <thead>
          <tr>
            <Box
              component='th'
              rowSpan={2}
              sx={{
                ...th,
                textAlign: 'left',
                position: 'sticky',
                left: 0,
                bgcolor: '#fff',
                zIndex: 1,
                verticalAlign: 'bottom'
              }}
            >
              Student
            </Box>
            {PANELS.map((p) => [
              <Box component='th' key={`${p.key}-g`} sx={gap} rowSpan={2} />,
              <Box
                component='th'
                key={p.key}
                colSpan={order.length}
                sx={{
                  ...th,
                  fontSize: 12,
                  color: SUMMARY_PALETTE.ink,
                  borderBottom: `2px solid ${SUMMARY_PALETTE.cardBorder}`
                }}
              >
                {p.title}
              </Box>
            ])}
          </tr>
          <tr>
            {PANELS.map((p) =>
              order.map((per) => (
                <Box
                  component='th'
                  key={`${p.key}-${per}`}
                  sx={{
                    ...th,
                    fontWeight: per === latest ? 700 : 500,
                    color: per === latest ? SUMMARY_PALETTE.ink : SUMMARY_PALETTE.muted
                  }}
                >
                  {per}
                </Box>
              ))
            )}
          </tr>
        </thead>
        <tbody>
          {list.map((s) => (
            <tr key={s.id}>
              <Box
                component='td'
                sx={{
                  fontSize: 13,
                  color: SUMMARY_PALETTE.ink,
                  px: 0.75,
                  py: 0.4,
                  whiteSpace: 'nowrap',
                  position: 'sticky',
                  left: 0,
                  bgcolor: '#fff',
                  borderTop: `1px solid ${SUMMARY_PALETTE.grid}`
                }}
              >
                {s.name}
              </Box>
              {PANELS.map((p) => [
                <Box component='td' key={`${p.key}-g`} sx={gap} />,
                ...order.map((per) => {
                  const c = s.byPeriod.get(per)
                  const v = cell(p.key, c)
                  return (
                    <Box
                      component='td'
                      key={`${p.key}-${per}`}
                      title={
                        c
                          ? `${per} · ${fmtDate(c.test_date)}${c.year_level ? ` · ${c.year_level}` : ''}`
                          : `${per}: no result`
                      }
                      sx={{
                        fontSize: 12.5,
                        textAlign: 'center',
                        px: 0.75,
                        py: 0.4,
                        whiteSpace: 'nowrap',
                        bgcolor: v.bg,
                        color: per === latest ? INK.primary || SUMMARY_PALETTE.ink : SUMMARY_PALETTE.ink,
                        fontWeight: per === latest ? 700 : 400,
                        borderTop: `1px solid ${SUMMARY_PALETTE.grid}`,
                        borderLeft: `1px solid ${SUMMARY_PALETTE.grid}`
                      }}
                    >
                      {v.text}
                    </Box>
                  )
                })
              ])}
            </tr>
          ))}
        </tbody>
      </Box>
      {!model.anyStanine && (
        <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, mt: 1 }}>{STANINE_NOTE}</Typography>
      )}
      <Typography sx={{ fontSize: 11, color: SUMMARY_PALETTE.faint, mt: 1.5, lineHeight: 1.5 }}>
        Read across a row: score history, relative-standing history (stanine) and band history stay separate
        and aligned. Score colour uses one range across all periods; the latest period is bold.
      </Typography>
    </Box>
  )
}

export default PatProgressPanelsCard
